import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { TFunction } from "i18next";
import {
  MyTimesheetFilter,
  MyTimesheetMyRequestsDashboardSummary,
  MyTimesheetMyRequestsDashboardTable,
} from "my-timesheet/components";
import {
  useMyTimesheetFilterState,
  useMyTimesheetTimesheetList,
} from "my-timesheet/hooks";
import { OcCard, OcChart, OcChartData, OcIconForState } from "oc/components";
import { RoleCategoriesT } from "oc/configs/constants";
import {
  useOcButtonGroup,
  useOcCurrentUserState,
  useOcRoleFilters,
} from "oc/hooks";
import { RoleFilterInput } from "oc/types";

ChartJS.register(ArcElement, Tooltip, Legend);

export interface SummaryItem {
  name: string;
  summaryTime?: string;
  color?: string;
  [key: string]: any;
}

type TypeFilters = {
  project: string[];
  client: string[];
  cost: string[];
};

export interface MyTimesheetWorktimeReportProps {
  periodStart: string;
  periodEnd: string;
  role: string;
  isMobile: boolean;
}

export const MyTimesheetWorktimeReport: React.FC<
  MyTimesheetWorktimeReportProps
> = ({ periodStart, periodEnd, role, isMobile }) => {
  const { t }: { t: TFunction } = useTranslation();

  const buttonsDef = ["MY_TIMESHEET_PROJECT", "MY_TIMESHEET_CLIENT", "OC_COST"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MySheetMyRequestsDashboard",
    buttonsDef,
  });

  const { state: pendingTypeFilters } = useMyTimesheetFilterState();
  const { contractCode: contract } = useOcCurrentUserState();
  const product = "MyTimesheet";
  const { roleFilters: pendingRoleFilters } = useOcRoleFilters({
    contract,
    product,
    role,
    id: "MyTimesheetContractsFilter",
  });

  const cloneTypeFilters = useCallback((src: any): TypeFilters => {
    const s = (src as TypeFilters) || { project: [], client: [], cost: [] };
    return {
      project: Array.isArray(s.project) ? [...s.project] : [],
      client: Array.isArray(s.client) ? [...s.client] : [],
      cost: Array.isArray(s.cost) ? [...s.cost] : [],
    };
  }, []);

  const cloneRoleFilters = useCallback((src: any): RoleFilterInput => {
    const rf = (src as RoleFilterInput) || ({} as RoleFilterInput);
    const out: any = {};
    Object.entries(rf).forEach(([k, v]) => {
      out[k] = Array.isArray(v) ? [...v] : Array.isArray((v as any)?.items) ? [...(v as any).items] : v ?? [];
    });
    return out as RoleFilterInput;
  }, []);

  const [appliedTypeFilters, setAppliedTypeFilters] = useState<TypeFilters>(
    cloneTypeFilters(pendingTypeFilters)
  );

  const [appliedRoleFilters, setAppliedRoleFilters] = useState<RoleFilterInput>(
    cloneRoleFilters(pendingRoleFilters)
  );

  useEffect(() => {
    setAppliedTypeFilters(cloneTypeFilters(pendingTypeFilters));
    setAppliedRoleFilters(cloneRoleFilters(pendingRoleFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    summaryByType,
    hoursSummary,
    hoursSummaryBillable,
    costsSummary,
    loading,
    error,
  } = useMyTimesheetTimesheetList({
    contractId: "",
    periodStart: new Date(periodStart),
    periodEnd: new Date(periodEnd),
    roleFilters: appliedRoleFilters as RoleFilterInput,
    typeFilters: appliedTypeFilters as any,
    state: "approved",
    skipQuery: false,
    role,
  });

  let data: SummaryItem[] = [];

  const projectData = useMemo(() => summaryByType("projectName"), [summaryByType]);
  const clientData = useMemo(() => summaryByType("clientName"), [summaryByType]);
  const costData = useMemo(() => summaryByType("costName"), [summaryByType]);

  const HUF = new Intl.NumberFormat("hu-HU", {
    style: "currency",
    currency: "HUF",
    maximumSignificantDigits: 3,
  });

  const getTimeSummary = (value: string): string => {
    const [hours, minutes] = value.split(":");
    const hLabel = `${hours} ${t("OC_HOUR").toLowerCase()}`;
    if (minutes === "00") return hLabel;
    return `${hLabel} ${minutes} ${t("MY_TIMESHEET_MINUTE").toLowerCase()}`;
  };

  let chartSummary = "";
  switch (selectedButton) {
    case "MY_TIMESHEET_PROJECT":
      chartSummary = getTimeSummary(hoursSummary);
      data = projectData;
      break;
    case "MY_TIMESHEET_CLIENT":
      chartSummary = getTimeSummary(hoursSummary);
      data = clientData;
      break;
    case "OC_COST":
      chartSummary = HUF.format(costsSummary);
      data = costData;
      break;
    default:
      break;
  }

  const newChartData: OcChartData[] = data.map((item) => {
    const [hours, minutes] = (item?.summaryTime ?? "0:00").split(":");
    const value = Number.parseInt(hours) * 60 + Number.parseInt(minutes);
    return {
      color: item.color!,
      label: item.name,
      value,
    };
  });

  const handleApplyFilters = useCallback(() => {
    setAppliedTypeFilters(cloneTypeFilters(pendingTypeFilters));
    setAppliedRoleFilters(cloneRoleFilters(pendingRoleFilters));
  }, [cloneRoleFilters, cloneTypeFilters, pendingRoleFilters, pendingTypeFilters]);

  const handleResetFilters = useCallback(() => {
    setAppliedTypeFilters({ project: [], client: [], cost: [] });
    const keys = Object.keys((pendingRoleFilters as any) || {});
    const emptyRoleFilters = keys.reduce((acc: any, k) => {
      acc[k] = [];
      return acc;
    }, {});
    setAppliedRoleFilters(emptyRoleFilters as RoleFilterInput);
  }, [pendingRoleFilters]);
  const handleDisabledToTrue = useCallback(() => {
    handleApplyFilters();
  }, [handleApplyFilters]);

  return (
    <Box>
      <MyTimesheetFilter
        data={null}
        setData={() => { }}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onDisabledToTrue={handleDisabledToTrue}
        contract={contract}
        product={product as any}
        role={role as RoleCategoriesT}
      />
      <Box height={16} />
      {data.length === 0 ? (
        <>
          <Box height={32} />
          <OcIconForState
            data={data}
            loading={loading}
            isMobile={isMobile}
            error={!!error}
            module="MyTimesheet"
          />
        </>
      ) : (
        <>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems="center"
          >
            {[
              { label: "MY_TIMESHEET_TOTAL_TIME", value: hoursSummary },
              {
                label: "MY_TIMESHEET_TOTAL_TIME_BILLABLE",
                value: hoursSummaryBillable,
              },
              { label: "MY_TIMESHEET_TOTAL_COST", value: costsSummary },
            ].map((item, idx) => (
              <Box key={idx} sx={{ width: isMobile ? "100%" : "calc(33% - 8px)" }}>
                <MyTimesheetMyRequestsDashboardSummary
                  label={t(item.label)}
                  value={item.value}
                />
                {isMobile && <Box height={16} />}
              </Box>
            ))}
          </Box>
          <Box height={16} />
          <OcCard
            label={t("MY_TIMESHEET_SUMMARY_STATEMENT").toString()}
            loading={loading}
            error={!!error}
          >
            <Box height={22} />
            <Box display="flex" justifyContent="center">
              <OcButtonGroup />
            </Box>
            <Box height={38} />
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              alignItems={isMobile ? "center" : "flex-start"}
            >
              <Box flexGrow={1} pr={isMobile ? 0 : 4}>
                <MyTimesheetMyRequestsDashboardTable
                  data={data}
                  type={selectedButton}
                  loading={loading}
                  isMobile={isMobile}
                />
              </Box>
              {isMobile && <Box height={32} />}
              <Box>
                <OcChart
                  data={newChartData}
                  summaryTitle={chartSummary}
                  summaryText={t("OC_SUMMARY")}
                  variant="MyTimesheet"
                />
              </Box>
            </Box>
          </OcCard>
        </>
      )}
    </Box>
  );
};

export default MyTimesheetWorktimeReport;
